/**
 * Persistence + shaping helpers for the /qa/hero/perf dashboard.
 *
 * Samples come from the `hero-perf` CustomEvent emitted by
 * `instrumentHeroImage()` (see `src/lib/hero-perf.ts`). We store them in
 * localStorage so timings collected across sessions, devices, and network
 * profiles can be compared over time.
 */

import type { HeroPerfReport } from "./hero-perf";

export type Breakpoint = "mobile" | "tablet" | "desktop";

export type HeroPerfSample = {
  id: string;
  /** ISO timestamp of capture. */
  at: string;
  /** Viewport label used during the run, e.g. "iPhone 14 (390)". */
  label: string;
  breakpoint: Breakpoint;
  viewportWidth: number;
  dpr: number;
  connection: string;
  rttMs?: number;
  downlinkMbps?: number;
  /** Absolute phase times (ms since timeOrigin of the measured document). */
  start?: number;
  responseEnd?: number;
  load?: number;
  decoded?: number;
  lcp?: number;
  /** Deltas relative to `start` — what the charts plot. */
  networkMs?: number;
  loadMs?: number;
  decodeMs?: number;
  totalMs?: number;
  lcpMs?: number;
  transferKb?: number;
  currentSrc: string;
};

export const SAMPLES_STORAGE_KEY = "qa-hero-perf-samples-v1";

export function bucketBreakpoint(width: number): Breakpoint {
  if (width < 640) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

const round = (n?: number) =>
  typeof n === "number" && Number.isFinite(n) ? +n.toFixed(1) : undefined;

/** Converts a raw report into a flat, chartable sample row. */
export function toSample(
  report: HeroPerfReport,
  meta: { label?: string; connectionOverride?: string } = {},
): HeroPerfSample {
  const width = report.viewportWidth;
  const start = report.timings.start;
  const rel = (t?: number) =>
    t != null && start != null ? +(t - start).toFixed(1) : undefined;

  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    at: new Date().toISOString(),
    label: meta.label ?? `${width}px`,
    breakpoint: bucketBreakpoint(width),
    viewportWidth: width,
    dpr: +(report.devicePixelRatio || 1).toFixed(2),
    connection:
      meta.connectionOverride ||
      report.connection?.effectiveType ||
      "unknown",
    rttMs: report.connection?.rttMs,
    downlinkMbps: report.connection?.downlinkMbps,
    start: round(start),
    responseEnd: rel(report.timings.responseEnd),
    load: rel(report.timings.load),
    decoded: rel(report.timings.decoded),
    lcp: rel(report.timings.lcp),
    networkMs: round(report.deltas.networkMs),
    loadMs: round(report.deltas.loadMs),
    decodeMs: round(report.deltas.decodeMs),
    totalMs: round(report.deltas.totalMs),
    lcpMs: round(report.deltas.lcpMs),
    transferKb:
      report.transferSizeBytes != null
        ? +(report.transferSizeBytes / 1024).toFixed(1)
        : undefined,
    currentSrc: report.currentSrc,
  };
}

export function loadSamples(): HeroPerfSample[] {
  try {
    const raw = localStorage.getItem(SAMPLES_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as HeroPerfSample[]) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function persistSamples(samples: HeroPerfSample[]) {
  try {
    localStorage.setItem(SAMPLES_STORAGE_KEY, JSON.stringify(samples));
  } catch {
    /* storage unavailable / quota */
  }
}

export const CSV_COLUMNS: (keyof HeroPerfSample)[] = [
  "at",
  "label",
  "breakpoint",
  "viewportWidth",
  "dpr",
  "connection",
  "rttMs",
  "downlinkMbps",
  "networkMs",
  "loadMs",
  "decodeMs",
  "totalMs",
  "lcpMs",
  "transferKb",
  "currentSrc",
];

export function samplesToCsv(samples: HeroPerfSample[]): string {
  const head = CSV_COLUMNS.join(",");
  const rows = samples.map((s) =>
    CSV_COLUMNS.map((c) => {
      const v = s[c];
      if (v == null) return "";
      const str = String(v);
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    }).join(","),
  );
  return [head, ...rows].join("\n");
}

/** Mean of a numeric field, ignoring undefined values. */
export function mean(samples: HeroPerfSample[], key: keyof HeroPerfSample) {
  const vals = samples
    .map((s) => s[key])
    .filter((v): v is number => typeof v === "number");
  if (!vals.length) return undefined;
  return +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
}

/** p75 — the percentile Core Web Vitals reports against. */
export function p75(samples: HeroPerfSample[], key: keyof HeroPerfSample) {
  const vals = samples
    .map((s) => s[key])
    .filter((v): v is number => typeof v === "number")
    .sort((a, b) => a - b);
  if (!vals.length) return undefined;
  const idx = Math.min(vals.length - 1, Math.ceil(vals.length * 0.75) - 1);
  return +vals[idx].toFixed(1);
}

/** Groups samples by a dimension and returns averaged phase deltas. */
export function groupSeries(
  samples: HeroPerfSample[],
  dimension: "breakpoint" | "dpr" | "connection" | "label",
) {
  const buckets = new Map<string, HeroPerfSample[]>();
  for (const s of samples) {
    const key = String(s[dimension]);
    const arr = buckets.get(key) ?? [];
    arr.push(s);
    buckets.set(key, arr);
  }
  return [...buckets.entries()]
    .map(([key, arr]) => ({
      key,
      count: arr.length,
      networkMs: mean(arr, "networkMs") ?? 0,
      loadMs: mean(arr, "loadMs") ?? 0,
      decodeMs: mean(arr, "decodeMs") ?? 0,
      totalMs: mean(arr, "totalMs") ?? 0,
      lcpMs: mean(arr, "lcpMs") ?? 0,
      lcpP75: p75(arr, "lcpMs"),
      totalP75: p75(arr, "totalMs"),
    }))
    .sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true }));
}
